import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { hasPermission, isRoleAtLeast, Permissions, ROLE_LABELS } from '../hooks/usePermissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [effectiveRole, setEffectiveRole] = useState(null);
  const [sessionRole, setSessionRole] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [requiresRoleSelection, setRequiresRoleSelection] = useState(false);

  // Refresh user data including impersonation status
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      setUser(userData);
      setUserRoles(userData.roles || [userData.role]);
      setRequiresRoleSelection(userData.requires_role_selection || false);
      
      // Check for session role
      if (userData.session_role) {
        setSessionRole(userData.session_role);
      }
      
      // Check for impersonation
      if (userData.is_impersonating && userData.effective_role) {
        setIsImpersonating(true);
        setEffectiveRole(userData.effective_role);
      } else {
        setIsImpersonating(false);
        setEffectiveRole(null);
      }
    } catch (e) {
      setUser(null);
      setIsImpersonating(false);
      setEffectiveRole(null);
      setSessionRole(null);
      setUserRoles([]);
      setRequiresRoleSelection(false);
    }
  }, []);

  useEffect(() => {
    // Check for existing session via /auth/me (cookie-based authentication)
    authAPI.getMe()
      .then(response => {
        const userData = response.data;
        setUser(userData);
        setUserRoles(userData.roles || [userData.role]);
        setRequiresRoleSelection(userData.requires_role_selection || false);
        
        // Check for session role
        if (userData.session_role) {
          setSessionRole(userData.session_role);
        }
        
        // Check for impersonation status
        if (userData.is_impersonating && userData.effective_role) {
          setIsImpersonating(true);
          setEffectiveRole(userData.effective_role);
        }
      })
      .catch(() => {
        // Session invalid or no cookie, user is not authenticated
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    // Two-step MFA: when the backend requires it, don't set the user —
    // just hand the challenge token back so the Login page can collect
    // the 6-digit code.
    if (response.data?.mfa_required) {
      return { mfa_required: true, mfa_token: response.data.mfa_token };
    }
    const { user: userData, roles, requires_role_selection } = response.data;
    // Cookie is set automatically by the backend via httpOnly cookie
    setUser(userData);
    setUserRoles(roles || [userData.role]);
    setRequiresRoleSelection(requires_role_selection || false);
    setIsImpersonating(false);
    setEffectiveRole(null);
    setSessionRole(null);
    return { user: userData, roles: roles || [userData.role], requires_role_selection };
  };

  const verifyMfaLogin = async (mfa_token, code) => {
    const response = await authAPI.mfaVerifyLogin(mfa_token, code);
    const { user: userData, roles, requires_role_selection } = response.data;
    setUser(userData);
    setUserRoles(roles || [userData.role]);
    setRequiresRoleSelection(requires_role_selection || false);
    setIsImpersonating(false);
    setEffectiveRole(null);
    setSessionRole(null);
    return { user: userData, roles: roles || [userData.role], requires_role_selection };
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { user: userData } = response.data;
    // Cookie is set automatically by the backend via httpOnly cookie
    setUser(userData);
    setUserRoles([userData.role]);
    setRequiresRoleSelection(false);
    setIsImpersonating(false);
    setEffectiveRole(null);
    setSessionRole(null);
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignore errors, still clear local state
    }
    setUser(null);
    setUserRoles([]);
    setRequiresRoleSelection(false);
    setIsImpersonating(false);
    setEffectiveRole(null);
    setSessionRole(null);
  };

  // Select role for session (multi-role users)
  const selectRole = async (role) => {
    const response = await authAPI.selectRole(role);
    setSessionRole(response.data.session_role);
    setRequiresRoleSelection(false);
    return response.data;
  };

  // Role impersonation (Admin only)
  const startImpersonation = async (role) => {
    if (user?.role !== 'admin') {
      throw new Error('Only admins can impersonate roles');
    }
    
    const response = await authAPI.impersonate(role);
    setIsImpersonating(true);
    setEffectiveRole(response.data.effective_role);
    return response.data;
  };

  const stopImpersonation = async () => {
    if (!isImpersonating) return;
    
    await authAPI.stopImpersonate();
    setIsImpersonating(false);
    setEffectiveRole(null);
  };

  // Get the effective role (impersonated > session role > actual)
  const getEffectiveRole = () => {
    if (isImpersonating && effectiveRole) {
      return effectiveRole;
    }
    if (sessionRole) {
      return sessionRole;
    }
    return user?.role;
  };

  // Role checks using effective role
  const actualRole = user?.role;
  const currentRole = getEffectiveRole();
  
  // Normalize role for backwards compatibility
  const normalizeRole = (r) => r === 'team_leader' ? 'manager' : r;
  
  const isAdmin = () => actualRole === 'admin'; // Always check actual role for admin status
  const isManager = () => normalizeRole(currentRole) === 'manager';
  const isTeamLeader = () => isManager(); // Backwards compatibility
  const isMedic = () => currentRole === 'medic';
  const isOpsCoordinator = () => currentRole === 'ops_coordinator' || currentRole === 'standard';
  const isStandard = () => isOpsCoordinator(); // Backwards compatibility
  
  // Hierarchy checks using effective role
  const hasManagerAccess = () => isRoleAtLeast(normalizeRole(currentRole), 'manager');
  const hasOpsAccess = () => isRoleAtLeast(normalizeRole(currentRole), 'ops_coordinator');
  
  // Permission checks using effective role
  const checkPermission = (permission) => hasPermission(normalizeRole(currentRole), permission);
  const canApproveDocuments = () => checkPermission(Permissions.APPROVE_DOCUMENTS);
  const canExportCSV = () => checkPermission(Permissions.EXPORT_CSV);
  const canAccessAuditLogs = () => checkPermission(Permissions.ACCESS_AUDIT_LOGS);
  const canAccessSettings = () => checkPermission(Permissions.ACCESS_SETTINGS);

  // Platform admin (cross-tenant operator). This is a FLAG, independent of
  // the tenant role — a user can be both a tenant admin and a platform admin.
  const isPlatformAdmin = () => user?.is_platform_admin === true;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      verifyMfaLogin,
      register,
      logout,
      // Multi-role support
      userRoles,
      requiresRoleSelection,
      sessionRole,
      selectRole,
      // Role checks
      isAdmin,
      isManager,
      isTeamLeader,
      isMedic,
      isOpsCoordinator,
      isStandard,
      // Hierarchy checks
      hasManagerAccess,
      hasOpsAccess,
      // Permission checks
      checkPermission,
      canApproveDocuments,
      canExportCSV,
      canAccessAuditLogs,
      canAccessSettings,
      // Platform admin
      isPlatformAdmin,
      // Impersonation
      isImpersonating,
      effectiveRole,
      actualRole,
      getEffectiveRole,
      startImpersonation,
      stopImpersonation,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
